import axios from 'axios';
import get from 'lodash/get';

import {
  PAYFAC_CARDS,
  PAYFAC_CARD_DELETE,
  PAYFAC_CARD_AUTOPAY,
  PAYFAC_CARD_DIRECT_CHARGE,
  PAYFAC_TOKENIZATION_URL,
  PAYFAC_TOKENIZATION_EMAIL,
  PAYFAC_CLIENT_CARD,
  CLIENT_PAYFAC_CARDS,
  CLIENT_PAYFAC_CARD_AUTOPAY,
  CLIENT_PAYFAC_PAY
} from '../constants/endpoints';
import { parseUrl } from '../libs/utils';

/**
 * Obtiene la URL de tokenización para que el cliente ingrese su tarjeta.
 * GET /api/v1/payfac/cards/:customerUuid/tokenization-url
 * @param {string} customerUuid
 * @returns {Promise<string>} url de tokenización
 */
export const getTokenizationUrl = async customerUuid => {
  const url = parseUrl(PAYFAC_TOKENIZATION_URL, { customerUuid });
  const res = await axios.get(url);
  return get(res, 'data.data.url', '');
};

/**
 * Obtiene la URL de tokenización con source=app para redirigir desde el operador.
 * El back construye la URL de Paystand con returnUrl apuntando a /app/customer/:uuid?card_token=true
 * GET /api/v1/payfac/cards/:customerUuid/tokenization-url?source=app
 * @param {string} customerUuid
 * @returns {Promise<string>} url de tokenización
 */
export const getTokenizationRedirectUrl = async customerUuid => {
  const url = parseUrl(PAYFAC_TOKENIZATION_URL, { customerUuid });
  const res = await axios.get(url, { params: { source: 'app' } });
  return get(res, 'data.data.url', '');
};

/**
 * Envía la URL de tokenización por email al cliente.
 * POST /api/v1/payfac/cards/:customerUuid/tokenization-email
 * @param {string} customerUuid
 * @param {string[]} emails
 * @returns {Promise<void>}
 */
export const sendTokenizationEmail = async (customerUuid, emails) => {
  const url = parseUrl(PAYFAC_TOKENIZATION_EMAIL, { customerUuid });
  await axios.post(url, { emails });
};

/**
 * Lista las tarjetas guardadas del cliente con paginación.
 * GET /api/v1/payfac/cards/:customerUuid?page=N
 * @param {string} customerUuid
 * @param {number} page
 * @returns {Promise<{ data: Array, count: number, pages: number }>}
 */
export const getCustomerCards = async (customerUuid, page = 1) => {
  const url = parseUrl(PAYFAC_CARDS, { customerUuid });
  const res = await axios.get(url, { params: { page } });
  return {
    data: get(res, 'data.data', []),
    count: get(res, 'data.count', 0),
    pages: get(res, 'data.pages', 0)
  };
};

/**
 * Elimina una tarjeta guardada del cliente.
 * DELETE /api/v1/payfac/cards/:customerUuid/:cardUuid
 * @param {string} customerUuid
 * @param {string} cardUuid
 * @returns {Promise<void>}
 */
export const deleteCard = async (customerUuid, cardUuid) => {
  const url = parseUrl(PAYFAC_CARD_DELETE, { customerUuid, cardUuid });
  await axios.delete(url);
};

/**
 * Activa o desactiva autopay para una tarjeta del cliente.
 * PATCH /api/v1/payfac/cards/:customerUuid/:cardUuid/autopay
 * @param {string} customerUuid
 * @param {string} cardUuid
 * @param {boolean} enabled
 * @returns {Promise<{ success: boolean, data?: { uuid: string, autopay: boolean }, errorCode?: string }>}
 */
export const toggleCardAutopay = async (customerUuid, cardUuid, enabled) => {
  try {
    const url = parseUrl(PAYFAC_CARD_AUTOPAY, { customerUuid, cardUuid });
    const res = await axios.patch(url, { enabled });
    return { success: true, data: get(res, 'data.data', {}) };
  } catch (error) {
    const key = get(error, 'response.data.key', 'INTERNAL_ERROR');
    return { success: false, errorCode: key };
  }
};


const DIRECT_CHARGE_ERROR_CODES = {
  PAYFAC_DIRECT_CHARGE_CARD_REQUIRED: 'PAYFAC_DIRECT_CHARGE_CARD_REQUIRED',
  PAYFAC_DIRECT_CHARGE_AMOUNT_REQUIRED: 'PAYFAC_DIRECT_CHARGE_AMOUNT_REQUIRED',
  PAYFAC_CARD_TOKEN_NOT_ENABLED: 'PAYFAC_CARD_TOKEN_NOT_ENABLED',
  PAYFAC_CUSTOMER_NOT_FOUND: 'PAYFAC_CUSTOMER_NOT_FOUND',
  PAYFAC_INTEGRATION_NOT_ACTIVE: 'PAYFAC_INTEGRATION_NOT_ACTIVE',
  PAYFAC_PAYER_ID_MISMATCH: 'PAYFAC_PAYER_ID_MISMATCH',
  PAYFAC_DIRECT_CHARGE_FAILED: 'PAYFAC_DIRECT_CHARGE_FAILED',
  INTERNAL_ERROR: 'INTERNAL_ERROR'
};

/**
 * Realiza un cobro directo a una tarjeta tokenizada.
 * POST /api/v1/payfac/cards/:customerUuid/direct-charge
 * @param {string} customerUuid
 * @param {string} fundId        — uuid de la tarjeta (card.uuid del listado)
 * @param {number} amount        — monto > 0
 * @param {string} [currency]    — default "MXN"
 * @param {string} [description] — default "Cobro directo con tarjeta"
 * @returns {Promise<{ success: boolean, data?: object, errorCode?: string }>}
 */
export const directCharge = async (customerUuid, fundId, amount, currency = 'MXN', description = 'Cobro directo con tarjeta') => {
  try {
    const url = parseUrl(PAYFAC_CARD_DIRECT_CHARGE, { customerUuid });
    const response = await axios.post(url, { card_uuid: fundId, amount, currency, description });
    return { success: true, data: get(response, 'data.data', {}) };
  } catch (error) {
    const key = get(error, 'response.data.key', DIRECT_CHARGE_ERROR_CODES.INTERNAL_ERROR);
    return { success: false, errorCode: key };
  }
};

const SAVE_CARD_ERROR_CODES = {
  CARD_ALREADY_EXISTS: 'CARD_ALREADY_EXISTS',
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  CARD_SAVE_ERROR: 'CARD_SAVE_ERROR'
};

/**
 * Lista las tarjetas guardadas del cliente desde el portal (sin autenticación de operador).
 * GET /api/v1/client/:customerUuid/cards/payfac?page=N
 * @param {string} customerUuid
 * @param {number} page
 * @returns {Promise<{ data: Array, count: number, pages: number }>}
 */
export const getClientPortalCards = async (customerUuid, page) => {
  const pageNum = page || 1;
  const url = parseUrl(CLIENT_PAYFAC_CARDS, { customerUuid });
  const res = await axios.get(url, { params: { page: pageNum } });
  return {
    data: get(res, 'data.data', []),
    count: get(res, 'data.count', 0),
    pages: get(res, 'data.pages', 0)
  };
};

/**
 * Cobra con una tarjeta guardada desde el portal del cliente.
 * POST /api/v1/client/:customerUuid/cards/payfac/pay
 * @param {string} customerUuid
 * @param {string} cardUuid
 * @param {Array}  invoices         — [{ uuid, amount_partial }]
 * @param {Array}  pendingInvoices  — [{ contract_uuid, amount_partial, issue_date, due_date, currency_rate }]
 * @returns {Promise<{ success: boolean, data?: object, errorCode?: string }>}
 */
export const chargePortalCard = async (customerUuid, cardUuid, invoices, pendingInvoices) => {
  try {
    const url = parseUrl(CLIENT_PAYFAC_PAY, { customerUuid });
    const body = { card_uuid: cardUuid, invoices };
    if (pendingInvoices && pendingInvoices.length > 0) body.pending_invoices = pendingInvoices;
    const res = await axios.post(url, body);
    return { success: true, data: get(res, 'data.data', {}) };
  } catch (error) {
    const errorCode = get(error, 'response.data.key', 'INTERNAL_ERROR');
    return { success: false, errorCode };
  }
};

/**
 * Activa o desactiva autopay para una tarjeta desde el portal del cliente (sin auth de operador).
 * PATCH /api/v1/client/:customerUuid/cards/payfac/:cardUuid/autopay
 * @param {string} customerUuid
 * @param {string} cardUuid
 * @param {boolean} enabled
 * @returns {Promise<{ success: boolean, data?: object, errorCode?: string }>}
 */
export const toggleClientPortalCardAutopay = async (customerUuid, cardUuid, enabled) => {
  try {
    const url = parseUrl(CLIENT_PAYFAC_CARD_AUTOPAY, { customerUuid, cardUuid });
    const res = await axios.patch(url, { enabled });
    return { success: true, data: get(res, 'data.data', {}) };
  } catch (error) {
    const status = error.response && error.response.status;
    if (status === 404) return { success: false, errorCode: 'NOT_FOUND' };
    if (status === 422) return { success: false, errorCode: 'CARD_EXPIRED' };
    return { success: false, errorCode: 'INTERNAL_ERROR' };
  }
};

/**
 * Guarda una tarjeta tokenizada en el portal del cliente.
 * POST /api/v1/client/:customerUuid/card/payfac
 * @param {string} customerUuid
 * @param {string} fundId — ps_fund_id devuelto por Paystand en el returnUrl
 * @param {string} [source] — origen del guardado (ej. 'app' cuando se llama desde el detalle de cliente, no desde el portal)
 * @returns {Promise<{ success: boolean, data?: object, errorCode?: string }>}
 */
export const saveCard = async (customerUuid, fundId, source) => {
  try {
    const url = parseUrl(PAYFAC_CLIENT_CARD, { customerUuid });
    const body = source ? { fundId, source } : { fundId };
    const response = await axios.post(url, body);
    return { success: true, data: response.data.data };
  } catch (error) {
    const status = error.response && error.response.status;
    const code = error.response && error.response.data && error.response.data.code;
    if (status === 409 || code === SAVE_CARD_ERROR_CODES.CARD_ALREADY_EXISTS)
      return { success: false, errorCode: SAVE_CARD_ERROR_CODES.CARD_ALREADY_EXISTS };
    if (status === 404 || code === SAVE_CARD_ERROR_CODES.CUSTOMER_NOT_FOUND)
      return { success: false, errorCode: SAVE_CARD_ERROR_CODES.CUSTOMER_NOT_FOUND };
    return { success: false, errorCode: SAVE_CARD_ERROR_CODES.CARD_SAVE_ERROR };
  }
};



// WEBPACK FOOTER //
// ./src/services/payfacCardService.js